import React from 'react';
import { Box, Container, Typography, Paper, Link } from '@mui/material';

const PrivacyPolicy = () => {
  return (
    <Box
      sx={{
        backgroundColor: '#f5f7fb',
        minHeight: '100vh',
        pb: 4,
        fontFamily: 'Arial, sans-serif'
      }}
    >
      <Box
        component="header"
        sx={{ backgroundColor: '#007f8b', color: '#fff', py: 4, textAlign: 'center' }}
      >
        <Typography variant="h1" sx={{ fontSize: '30px', fontWeight: 'bold', m: 0 }}>
          Privacy Policy
        </Typography>
        <Typography variant="subtitle1">UTrust Platform</Typography>
      </Box>

      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <Typography paragraph>
            <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
          </Typography>
          <Typography paragraph>
            This Privacy Policy outlines how <strong>UTrust Platform</strong> (“we”, “our”, “us”)
            collects, uses, discloses, and protects information when you access or use our website{' '}
            <Link
              href="https://www.ubumwetrust.com"
              target="_blank"
              rel="noopener"
              sx={{ color: '#007f8b' }}
            >
                https://www.ubumwetrust.com
            </Link>{' '}
            and our mobile or web applications (collectively, the “Services”).
          </Typography>
          <Typography paragraph>
            By using the Services, you agree to the terms in this Privacy Policy. If you do not
            agree, please discontinue the use of our Services. This policy does not constitute legal
            advice.
          </Typography>

          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            1. Information We Collect
          </Typography>

          <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            1.1 Personal Information
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>Name, email address, phone number</li>
            <li>Login details (email and hashed password)</li>
            <li>Business or organisational information</li>
          </Box>

          <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            1.2 Workforce & Usage Data
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>Clock-in / clock-out time records</li>
            <li>Shift schedules and attendance</li>
            <li>Activity and usage logs within the platform</li>
            <li>Support worker or staff-related information entered by your organisation</li>
          </Box>

          <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            1.3 Technical Information
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>IP address, device type, operating system</li>
            <li>App performance data and crash logs</li>
            <li>Cookies and similar tracking tools</li>
          </Box>

          <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            1.4 Information You Provide Directly
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>Uploaded documents or files</li>
            <li>Support requests and communication history</li>
            <li>Feedback and survey responses</li>
          </Box>

          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            2. How We Use Your Information
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>To create and manage user accounts</li>
            <li>To enable core functions (shift scheduling, clock-in/out, staffing tools)</li>
            <li>To communicate regarding updates, support, or service changes</li>
            <li>To ensure security and prevent unauthorised access</li>
            <li>To improve the functionality and performance of the Services</li>
            <li>To comply with regulations or legal obligations</li>
          </Box>

          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            3. Sharing of Information
          </Typography>
          <Typography paragraph>
            We do <strong>not</strong> sell your data. We may share information with:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>Service providers (hosting, analytics, communication tools)</li>
            <li>Administrators or managers within your organisation</li>
            <li>Legal or regulatory authorities when required</li>
            <li>Other parties with your explicit consent</li>
          </Box>

          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            4. Cookies & Tracking Technologies
          </Typography>
          <Typography paragraph>
            Cookies are used to maintain sessions, store preferences, and analyse usage patterns.
            You may disable cookies through browser settings, but some features may not function
            properly.
          </Typography>

          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            5. Data Retention
          </Typography>
          <Typography paragraph>
            We retain information for as long as necessary to provide the Services, fulfil legal
            obligations, resolve disputes, and maintain records. Data that is no longer required
            will be securely deleted or anonymised.
          </Typography>

          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            6. Security
          </Typography>
          <Typography paragraph>
            We implement administrative, technical, and physical safeguards to protect your
            information. However, no method of storage or transmission is completely secure.
          </Typography>

          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            7. International Transfers
          </Typography>
          <Typography paragraph>
            Your data may be stored or processed in countries outside your own. Where required, we
            ensure that appropriate protections are in place.
          </Typography>

          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            8. Your Rights
          </Typography>
          <Typography paragraph>Depending on your region, your rights may include:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>Accessing your personal information</li>
            <li>Requesting corrections or updates</li>
            <li>Deletion of data where legally applicable</li>
            <li>Restricting certain processing activities</li>
            <li>Withdrawing consent where consent is used as the legal basis</li>
          </Box>
          <Typography paragraph>
            To exercise these rights, please contact us using the information below. Identity
            verification may be required.
          </Typography>

          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            9. Children’s Privacy
          </Typography>
          <Typography paragraph>
            Our Services are intended for adults and organisations. We do not knowingly collect data
            from children under 16. If submitted accidentally, please contact us to remove it.
          </Typography>

          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            10. Third-Party Links
          </Typography>
          <Typography paragraph>
            Our Services may contain links to third-party sites. We are not responsible for the
            privacy practices of these external services.
          </Typography>

          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            11. Contact Us
          </Typography>
          <Typography paragraph>
            For any questions regarding this Privacy Policy, please contact us at:
            <br />
            <strong>Email:</strong>{' '}
            <Link href="mailto:family.mwebeyatrust@gmail.com" sx={{ color: '#007f8b' }}>
              family.mwebeyatrust@gmail.com
            </Link>
            <br />
            <strong>Website:</strong>{' '}
            <Link
              href="https://www.ubumwetrust.com"
              target="_blank"
              rel="noopener"
              sx={{ color: '#007f8b' }}
            >
              https://www.ubumwetrust.com
            </Link>
          </Typography>

          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            12. Changes to This Policy
          </Typography>
          <Typography paragraph>
            We may update this Privacy Policy from time to time. Changes will be posted on this page
            with an updated “Last Updated” date.
          </Typography>
        </Paper>
      </Container>

      <Box component="footer" sx={{ textAlign: 'center', p: 2, color: '#666', fontSize: '13px' }}>
        © {new Date().getFullYear()} UTrust Platform. All rights reserved.
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;